import React from 'react';

/**
 * @fileoverview Alert System Data Models
 * 
 * This file defines the core data models for the DeepRDashboard Alert System.
 * It includes TypeScript-like interfaces (using JSDoc for type checking),
 * validation functions, and utility functions for working with alert data.
 * 
 * The models follow the specifications from the alert-system-design.md document.
 */

/**
 * Enum for alert types
 * @readonly
 * @enum {string}
 */
const AlertType = {
  GLOBAL: 'Global',
  SYSTEM_EVENT: 'SystemEvent',
  PROCESS: 'Process',
  LOG: 'Log'
};

/**
 * Enum for alert severity levels
 * @readonly
 * @enum {string}
 */
const AlertSeverity = {
  INFO: 'Info',
  WARNING: 'Warning',
  CRITICAL: 'Critical'
};

/**
 * Enum for alert scope types
 * @readonly
 * @enum {string}
 */
const AlertScopeType = {
  GLOBAL: 'global',
  SERVER_SPECIFIC: 'server-specific'
};

/**
 * Enum for alert conditions
 * @readonly
 * @enum {string}
 */
const AlertCondition = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'NotEquals',
  GREATER_THAN: 'GreaterThan',
  LESS_THAN: 'LessThan',
  GREATER_THAN_OR_EQUALS: 'GreaterThanOrEquals',
  LESS_THAN_OR_EQUALS: 'LessThanOrEquals',
  CONTAINS: 'Contains'
};

/**
 * Enum for alert actions
 * @readonly
 * @enum {string}
 */
const AlertAction = {
  NOTIFICATION: 'Notification',
  EMAIL: 'Email',
  SLACK: 'Slack'
};

/**
 * Global events that can trigger alerts
 * @readonly
 * @enum {string}
 */
const GlobalEvents = [
  'Websocket.Connected',
  'Websocket.Disconnected',
  'Server.Connected',
  'Server.Disconnected'
];

/**
 * Process events that can trigger alerts
 * @readonly
 * @enum {string}
 */
const ProcessEvents = [
  'memory_usage',
  'cpu_usage',
  'disk_usage'
];

/**
 * System events that can trigger alerts
 * @readonly
 * @enum {string}
 */
const SystemEventEvents = [
  'cpu.percent',
  'disk.free',
  'disk.percent',
  'memory.available',
  'memory.percent',
  // Original cumulative network metrics (total since boot)
  'network.bytes_sent',
  'network.bytes_recv',
  'network.packets_sent',
  'network.packets_recv',
  // Rate-based network metrics (per second)
  'network.bytes_sent_rate',
  'network.bytes_recv_rate',
  'network.total_bytes_rate',
  'network.packets_sent_rate',
  'network.packets_recv_rate',
  'network.total_packets_rate'
];

/**
 * Log events that can trigger alerts
 * @readonly
 * @enum {string}
 */
const LogEvents = [
  'level',
  'message'
];

/**
 * @typedef {Object} AlertRule
 * @property {string} event - The event to monitor (e.g., 'cpu.percent', 'memory.used')
 * @property {string} condition - The condition to check (e.g., 'Equals', 'GreaterThan')
 * @property {*} value - The value to compare against
 * @property {boolean} [processNameTargetingEnabled] - Whether the rule targets specific processes by name/pattern (only for Process alert type)
 * @property {string} [processNamePattern] - Process name pattern to match (used when processNameTargetingEnabled is true)
 */

/**
 * @typedef {Object} AlertScope
 * @property {string} type - The scope type ('global' or 'server-specific')
 * @property {string[]} serverIds - Array of server IDs (only used when type is 'server-specific')
 */

/**
 * @typedef {Object} AlertDefinition
 * @property {string} id - Unique identifier for the alert
 * @property {string} name - Name of the alert
 * @property {string} description - Description of the alert
 * @property {string} type - Type of the alert ('Global', 'SystemEvent', 'Process', 'Log')
 * @property {string} severity - Severity level ('Info', 'Warning', 'Critical')
 * @property {AlertScope} scope - Scope of the alert
 * @property {AlertRule[]} rules - Array of rules for the alert
 * @property {string[]} actions - Array of actions to take when the alert is triggered
 * @property {number} createdAt - Timestamp when the alert was created
 * @property {number} updatedAt - Timestamp when the alert was last updated
 * @property {boolean} enabled - Whether the alert is enabled
 * @property {string} userId - ID of the user who created the alert
 * @property {string} userName - Name of the user who created the alert
 */

/**
 * @typedef {Object} Notification
 * @property {string} id - Unique identifier for the notification
 * @property {string} alertId - ID of the alert that triggered this notification
 * @property {string} title - Short title for the notification
 * @property {string} message - Detailed message for the notification
 * @property {string} severity - Severity level ('Info', 'Warning', 'Critical')
 * @property {number} timestamp - Timestamp when the notification was created
 * @property {boolean} read - Whether the notification has been read
 * @property {Object} source - Information about the source of the notification
 * @property {string} source.type - Type of the source (e.g., 'SystemEvent', 'Process', 'Log')
 * @property {*} source.data - Additional data about the source
 * @property {string} userId - ID of the user who owns this notification
 */

/**
 * Creates a new alert definition with default values.
 * 
 * @param {Object} partialAlert - Partial alert definition
 * @returns {AlertDefinition} A new alert definition with default values
 */
function createAlert(partialAlert = {}) {
  return {
    id: generateUUID(),
    name: '',
    description: '',
    type: AlertType.GLOBAL,
    severity: AlertSeverity.INFO,
    scope: {
      type: AlertScopeType.GLOBAL,
      serverIds: []
    },
    rules: [],
    actions: [AlertAction.NOTIFICATION],
    createdAt: Date.now(),
    updatedAt: Date.now(),
    enabled: true,
    userId: '',
    userName: '',
    ...partialAlert
  };
}

/**
 * Creates a new notification object with default values.
 * 
 * @param {Object} partialNotification - Partial notification object
 * @returns {Notification} A new notification object with default values
 */
/**
 * Generate a UUID for both production and test environments
 * In tests, it will use the mocked crypto.randomUUID
 * @returns {string} A UUID v4 string
 */
function generateUUID() {
  // Always try to use crypto.randomUUID first, which will be mocked in tests
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  }
  
  // Fallback for environments without crypto support
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function createNotification(partialNotification = {}) {
  return {
    id: generateUUID(),
    alertId: '',
    title: '',
    message: '',
    severity: AlertSeverity.INFO,
    timestamp: Date.now(),
    read: false,
    source: {
      type: '',
      data: null
    },
    userId: '',
    ...partialNotification
  };
}

/**
 * Creates a new alert rule with default values.
 * 
 * @param {Object} partialRule - Partial rule object
 * @returns {AlertRule} A new alert rule with default values
 */
function createAlertRule(partialRule = {}) {
  return {
    event: '',
    condition: AlertCondition.EQUALS,
    value: '',
    processNameTargetingEnabled: false,
    processNamePattern: '',
    ...partialRule
  };
}

/**
 * Validates an alert definition.
 * 
 * @param {AlertDefinition} alert - The alert definition to validate
 * @returns {Object} Validation result with isValid and errors properties
 */
function validateAlert(alert) {
  const errors = {};

  // Required fields
  if (!alert.name || alert.name.trim() === '') {
    errors.name = 'Alert name is required';
  }

  if (!Object.values(AlertType).includes(alert.type)) {
    errors.type = 'Invalid alert type';
  }

  if (!Object.values(AlertSeverity).includes(alert.severity)) {
    errors.severity = 'Invalid severity level';
  }

  // Validate scope
  if (!alert.scope || !Object.values(AlertScopeType).includes(alert.scope.type)) {
    errors.scope = 'Invalid scope type';
  } else if (
    alert.scope.type === AlertScopeType.SERVER_SPECIFIC && 
    (!alert.scope.serverIds || !Array.isArray(alert.scope.serverIds) || alert.scope.serverIds.length === 0)
  ) {
    errors.serverIds = 'Server-specific scope requires at least one server ID';
  }

  // Validate rules
  if (!alert.rules || !Array.isArray(alert.rules) || alert.rules.length === 0) {
    errors.rules = 'At least one rule is required';
  } else {
    const ruleErrors = [];
    alert.rules.forEach((rule, index) => {
      const ruleValidation = validateAlertRule(rule, alert.type);
      if (!ruleValidation.isValid) {
        ruleErrors[index] = ruleValidation.errors;
      }
    });
    
    if (ruleErrors.length > 0) {
      errors.rules = ruleErrors;
    }
  }

  // Validate actions
  if (!alert.actions || !Array.isArray(alert.actions) || alert.actions.length === 0) {
    errors.actions = 'At least one action is required';
  } else {
    const invalidActions = alert.actions.filter(action => !Object.values(AlertAction).includes(action));
    if (invalidActions.length > 0) {
      errors.actions = `Invalid action(s): ${invalidActions.join(', ')}`;
    }
  }

  // Validate user
  if (!alert.userId) {
    errors.userId = 'User ID is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates an alert rule.
 * 
 * @param {AlertRule} rule - The rule to validate
 * @param {string} alertType - The type of the alert this rule belongs to
 * @returns {Object} Validation result with isValid and errors properties
 */
function validateAlertRule(rule, alertType) {
  const errors = {};

  // Validate condition
  if (!Object.values(AlertCondition).includes(rule.condition)) {
    errors.condition = 'Invalid condition';
  }

  // Validate event based on alert type
  switch (alertType) {
    case AlertType.GLOBAL:
      if (!GlobalEvents.includes(rule.event)) {
        errors.event = `Invalid event for Global alert type. Must be one of: ${GlobalEvents.join(', ')}`;
      }
      break;
    case AlertType.SYSTEM_EVENT:
      if (!SystemEventEvents.includes(rule.event)) {
        errors.event = `Invalid event for SystemEvent alert type. Must be one of: ${SystemEventEvents.join(', ')}`;
      }
      break;
    case AlertType.PROCESS:
      if (!ProcessEvents.includes(rule.event)) {
        errors.event = `Invalid event for Process alert type. Must be one of: ${ProcessEvents.join(', ')}`;
      }
      break;
    case AlertType.LOG:
      if (!LogEvents.includes(rule.event)) {
        errors.event = `Invalid event for Log alert type. Must be one of: ${LogEvents.join(', ')}`;
      }
      break;
    default:
      errors.event = 'Invalid alert type';
  }

  // Validate value
  if (rule.value === undefined || rule.value === null) {
    errors.value = 'Value is required';
  }

  // Validate process name targeting (only applicable for Process alert type)
  if (alertType === AlertType.PROCESS && rule.processNameTargetingEnabled) {
    if (!rule.processNamePattern || rule.processNamePattern.trim() === '') {
      errors.processNamePattern = 'Process Name/Pattern cannot be empty when targeting specific processes.';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates a notification object.
 * 
 * @param {Notification} notification - The notification to validate
 * @returns {Object} Validation result with isValid and errors properties
 */
function validateNotification(notification) {
  const errors = {};

  // Required fields
  if (!notification.alertId) {
    errors.alertId = 'Alert ID is required';
  }

  if (!notification.title || notification.title.trim() === '') {
    errors.title = 'Title is required';
  }

  if (!notification.message || notification.message.trim() === '') {
    errors.message = 'Message is required';
  }

  if (!Object.values(AlertSeverity).includes(notification.severity)) {
    errors.severity = 'Invalid severity level';
  }

  // Validate source
  if (!notification.source || !notification.source.type) {
    errors.source = 'Source type is required';
  }

  // Validate user
  if (!notification.userId) {
    errors.userId = 'User ID is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Gets the available events for a specific alert type.
 * 
 * @param {string} alertType - The type of alert
 * @returns {string[]} Array of available events for the given alert type
 */
function getEventsForAlertType(alertType) {
  switch (alertType) {
    case AlertType.GLOBAL:
      return [...GlobalEvents];
    case AlertType.SYSTEM_EVENT:
      return [...SystemEventEvents];
    case AlertType.PROCESS:
      return [...ProcessEvents];
    case AlertType.LOG:
      return [...LogEvents];
    default:
      return [];
  }
}

/**
 * Evaluates if an event satisfies an alert rule.
 * 
 * @param {AlertRule} rule - The rule to evaluate
 * @param {Object} event - The event to evaluate against the rule
 * @returns {boolean} True if the event satisfies the rule, false otherwise
 */
function evaluateRule(rule, event) {
  const eventValue = event[rule.event];
  
  // If the event doesn't have the specified property, rule is not satisfied
  if (eventValue === undefined) {
    return false;
  }

  switch (rule.condition) {
    case AlertCondition.EQUALS:
      return eventValue === rule.value;
    case AlertCondition.NOT_EQUALS:
      return eventValue !== rule.value;
    case AlertCondition.GREATER_THAN:
      return typeof eventValue === 'number' && eventValue > rule.value;
    case AlertCondition.LESS_THAN:
      return typeof eventValue === 'number' && eventValue < rule.value;
    case AlertCondition.GREATER_THAN_OR_EQUALS:
      return typeof eventValue === 'number' && eventValue >= rule.value;
    case AlertCondition.LESS_THAN_OR_EQUALS:
      return typeof eventValue === 'number' && eventValue <= rule.value;
    case AlertCondition.CONTAINS:
      return typeof eventValue === 'string' && eventValue.includes(rule.value);
    default:
      return false;
  }
}

/**
 * Evaluates if an event satisfies all rules in an alert definition.
 * 
 * @param {AlertDefinition} alert - The alert definition containing rules
 * @param {Object} event - The event to evaluate against the rules
 * @returns {boolean} True if the event satisfies all rules, false otherwise
 */
function evaluateAlert(alert, event) {
  // If the alert is disabled, it's not satisfied
  if (!alert.enabled) {
    return false;
  }

  // All rules must be satisfied
  return alert.rules.every(rule => evaluateRule(rule, event));
}

/**
 * Creates a notification from an alert and event that triggered it.
 * 
 * @param {AlertDefinition} alert - The alert that was triggered
 * @param {Object} event - The event that triggered the alert
 * @param {string} sourceType - The type of the event source
 * @returns {Notification} A new notification object
 */
function createNotificationFromAlert(alert, event, sourceType) {
  console.log('Creating notification from alert:', alert);
  console.log('Event data:', event);
  console.log('Source type:', sourceType);
  
  // Default title if alert name is undefined
  const defaultTitle = 'Alert Triggered';
  const alertName = alert.name || defaultTitle;
  
  // Build a rich, detailed message with actual values and context
  let message = '';
  let detailedInfo = '';
  
  // Add timestamp information if available
  const eventTime = event.timestamp ? new Date(event.timestamp).toLocaleString() : new Date().toLocaleString();
  
  // Extract server information if available
  const serverInfo = event.server_id || event.serverId || event.hostname || 'Unknown server';
  
  // Get rule information if available
  const rules = alert.rules || [];
  const ruleSummaries = [];
  
  // Get severity for formatting
  const severityIcon = typeof getSeverityIcon === 'function' ? getSeverityIcon(alert.severity) : null;
  const severityText = getSeverityDisplayText(alert.severity);
  
  // Format timestamp in a concise format (omitting seconds if they're 00)
  const eventDate = new Date(event.timestamp || Date.now());
  const timeStr = eventDate.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '');
  const conciseTime = timeStr.replace(':00$', '');  // Remove seconds if they're 00
  
  // Format the title based on source type - make it more descriptive but concise
  let enhancedTitle = alertName;
  if (sourceType === 'SystemEvent') {
    const eventName = event.event || 'System';
    enhancedTitle = `${severityText} ${alertName} - ${eventName}`;
  } else if (sourceType === 'Process') {
    const processName = event.name || 'Process';
    enhancedTitle = `${severityText} ${alertName} - ${processName}`;
  } else if (sourceType === 'Log') {
    const logLevel = event.level || event.log_level || 'Log';
    enhancedTitle = `${severityText} ${alertName} - ${logLevel}`;
  } else if (sourceType === 'Global') {
    const connType = event.type || event.connection_type || 'Connection';
    enhancedTitle = `${severityText} ${alertName} - ${connType}`;
  }
  
  // Helper function to format byte sizes - defined at the top for reuse
  function formatByteSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  // Build descriptive message based on source type
  if (sourceType === 'SystemEvent') {
    // For system events (CPU, memory, disk, etc.)
    const eventType = event.event || 'Unknown';
    const metrics = [];
    let primaryMetric = null;
    let primaryMetricValue = null;
    
    // Extract and format key metrics dynamically from event data
    // First, identify the primary metric that likely triggered the alert
    rules.forEach(rule => {
      if (rule.event === eventType) {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          if (event[field] !== undefined) {
            primaryMetric = field;
            primaryMetricValue = event[field];
          }
        });
      }
    });
    
    // Get all properties that might be metrics
    Object.entries(event).forEach(([key, value]) => {
      // Skip non-metric fields
      if (['id', 'server_id', 'serverId', 'event', 'timestamp', 'event_time', 'hostname'].includes(key)) {
        return;
      }
      
      // Format the display name
      const displayName = key.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Format the value based on its type and name
      let formattedValue = value;
      if (typeof value === 'number') {
        if (key.includes('percent')) {
          formattedValue = `${value.toFixed(2)}%`;
        } else if (key.includes('bytes') || key.includes('memory') || key.includes('disk')) {
          // Format sizes in appropriate units
          formattedValue = formatByteSize(value);
        } else {
          // Format other numbers with 2 decimal places
          formattedValue = value.toFixed(2);
        }
      }
      
      metrics.push(`${displayName}: ${formattedValue}`);
    });
    
    // Process all rules to show which conditions were met
    rules.forEach(rule => {
      if (rule.event === eventType) {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          const operator = condition.operator || '=';
          const threshold = condition.value !== undefined ? condition.value : 'N/A';
          const actualValue = event[field] !== undefined ? event[field] : 'N/A';
          
          // Only add if we have the actual value from the event
          if (actualValue !== 'N/A') {
            let valueFormatted = actualValue;
            let thresholdFormatted = threshold;
            
            // Format percentages
            if (field.includes('percent')) {
              valueFormatted = typeof actualValue === 'number' ? `${actualValue.toFixed(2)}%` : actualValue;
              thresholdFormatted = typeof threshold === 'number' ? `${threshold.toFixed(2)}%` : threshold;
            }
            
            // Create formatted field name
            const fieldDisplay = field.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            ruleSummaries.push(`${fieldDisplay} ${operator} ${thresholdFormatted} (Current: ${valueFormatted})`);
          }
        });
      }
    });
    
    // Create a concise, informative main message
    let primaryMetricInfo = '';
    if (primaryMetric && primaryMetricValue !== null) {
      // Format the primary metric value
      let formattedValue = primaryMetricValue;
      if (typeof primaryMetricValue === 'number') {
        if (primaryMetric.includes('percent')) {
          formattedValue = `${primaryMetricValue.toFixed(1)}%`; // Reduce to 1 decimal place
        } else if (primaryMetric.includes('bytes') || primaryMetric.includes('memory') || primaryMetric.includes('disk')) {
          formattedValue = formatByteSize(primaryMetricValue);
        } else {
          // Only show decimals if they're non-zero
          formattedValue = primaryMetricValue % 1 === 0 ? 
            primaryMetricValue.toString() : 
            primaryMetricValue.toFixed(1);
        }
      }
      
      // Format the metric name - shorten common words
      let metricName = primaryMetric
        .replace('percent', '%')
        .replace('memory', 'mem')
        .replace('usage', 'use')
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      
      primaryMetricInfo = `:${formattedValue}`;
    }
    
    message = `${eventType}${primaryMetricInfo} on ${serverInfo} | ${conciseTime}`;
    detailedInfo = metrics.length > 0 ? `\nMetrics: ${metrics.join(', ')}` : '';
    
  } else if (sourceType === 'Process') {
    // For process events
    const processName = event.name || 'Unknown';
    const processStatus = event.status || 'status change';
    const pid = event.pid !== undefined ? event.pid : 'N/A';
    
    // Extract process metrics dynamically
    const processMetrics = [];
    
    // Extract all process metrics dynamically
    Object.entries(event).forEach(([key, value]) => {
      // Skip non-metric fields and fields already displayed elsewhere
      if (['id', 'server_id', 'serverId', 'event', 'timestamp', 'event_time', 
           'hostname', 'name', 'status', 'pid'].includes(key)) {
        return;
      }
      
      // Format the display name
      const displayName = key.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Format the value based on its type and name
      let formattedValue = value;
      if (typeof value === 'number') {
        if (key.includes('percent')) {
          formattedValue = `${value.toFixed(2)}%`;
        } else if (key.includes('bytes') || key.includes('memory') || key.includes('disk')) {
          formattedValue = formatByteSize(value);
        } else {
          formattedValue = value.toFixed(2);
        }
      }
      
      processMetrics.push(`${displayName}: ${formattedValue}`);
    });
    
    // Process all rules to show which conditions were met
    rules.forEach(rule => {
      if (rule.event === 'process') {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          const operator = condition.operator || '=';
          const threshold = condition.value !== undefined ? condition.value : 'N/A';
          const actualValue = event[field] !== undefined ? event[field] : 'N/A';
          
          if (actualValue !== 'N/A') {
            let valueFormatted = actualValue;
            let thresholdFormatted = threshold;
            
            // Format values based on field type
            if (typeof actualValue === 'number') {
              if (field.includes('percent')) {
                valueFormatted = `${actualValue.toFixed(2)}%`;
                thresholdFormatted = typeof threshold === 'number' ? `${threshold.toFixed(2)}%` : threshold;
              } else if (field.includes('bytes') || field.includes('memory') || field.includes('disk')) {
                valueFormatted = formatByteSize(actualValue);
                thresholdFormatted = typeof threshold === 'number' ? formatByteSize(threshold) : threshold;
              } else {
                valueFormatted = actualValue.toFixed(2);
                thresholdFormatted = typeof threshold === 'number' ? threshold.toFixed(2) : threshold;
              }
            }
            
            // Format field name for display
            const fieldDisplay = field.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            
            ruleSummaries.push(`${fieldDisplay} ${operator} ${thresholdFormatted} (Current: ${valueFormatted})`);
          }
        });
      }
    });
    
    // Extract primary trigger metric
    let primaryMetric = null;
    let primaryMetricValue = null;
    
    // Identify the primary metric that likely triggered the alert
    rules.forEach(rule => {
      if (rule.event === 'process') {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          if (event[field] !== undefined) {
            primaryMetric = field;
            primaryMetricValue = event[field];
          }
        });
      }
    });
    
    // Create concise message format
    let primaryMetricInfo = '';
    if (primaryMetric && primaryMetricValue !== null) {
      let formattedValue = primaryMetricValue;
      if (typeof primaryMetricValue === 'number') {
        if (primaryMetric.includes('percent')) {
          formattedValue = `${primaryMetricValue.toFixed(1)}%`;
        } else if (primaryMetric.includes('bytes') || primaryMetric.includes('memory') || primaryMetric.includes('disk')) {
          formattedValue = formatByteSize(primaryMetricValue);
        } else {
          formattedValue = primaryMetricValue % 1 === 0 ? 
            primaryMetricValue.toString() : 
            primaryMetricValue.toFixed(1);
        }
      }
      
      primaryMetricInfo = `:${formattedValue}`;
    }
    
    // If status is important, add it to the message
    const statusInfo = (processStatus !== 'running' && processStatus !== 'status change') ? 
      `/${processStatus}` : '';
    
    message = `${processName}${pid !== 'N/A' ? `(${pid})` : ''}${statusInfo}${primaryMetricInfo} | ${conciseTime}`;
    
    // Format detailed info - only include status if not already in the message
    let detailsArr = [];
    if (processStatus !== 'status change') {
      detailsArr.push(`Status: ${processStatus}`);
    }
    
    // Add all metrics in a more concise format
    if (processMetrics.length > 0) {
      detailsArr.push(`Metrics: ${processMetrics.join(', ')}`);
    }
    
    detailedInfo = detailsArr.length > 0 ? '\n' + detailsArr.join('\n') : '';
    
  } else if (sourceType === 'Log') {
    // For log events
    // Extract key log properties while ensuring they're available
    const logLevel = event.level || event.log_level || 'N/A';
    const logMessage = event.message || event.log_message || event.content || 'N/A';
    const logFile = event.file || event.log_file || event.filename || 'N/A';
    
    // Extract all log properties dynamically
    const logProperties = [];
    
    // Primary log fields to always show at the top if available
    const primaryFields = ['level', 'log_level', 'file', 'log_file', 'filename', 'message', 'log_message', 'content'];
    const displayedFields = new Set();
    
    // Format the essential fields first
    if (logLevel !== 'N/A') {
      logProperties.push(`Level: ${logLevel}`);
      displayedFields.add('level');
      displayedFields.add('log_level');
    }
    
    if (logFile !== 'N/A') {
      logProperties.push(`File: ${logFile}`);
      displayedFields.add('file');
      displayedFields.add('log_file');
      displayedFields.add('filename');
    }
    
    if (logMessage !== 'N/A') {
      logProperties.push(`Message: ${logMessage}`);
      displayedFields.add('message');
      displayedFields.add('log_message');
      displayedFields.add('content');
    }
    
    // Process any additional fields
    Object.entries(event).forEach(([key, value]) => {
      // Skip fields we've already processed and common non-log fields
      if (displayedFields.has(key) || 
          ['id', 'server_id', 'serverId', 'event', 'timestamp', 'event_time', 'hostname'].includes(key)) {
        return;
      }
      
      // Format the display name
      const displayName = key.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Format the value based on type
      let formattedValue = value;
      if (typeof value === 'number') {
        formattedValue = value.toFixed(2);
      } else if (value === null || value === undefined) {
        formattedValue = 'N/A';
      } else if (typeof value === 'object') {
        try {
          formattedValue = JSON.stringify(value);
        } catch (e) {
          formattedValue = '[Complex Object]';
        }
      }
      
      logProperties.push(`${displayName}: ${formattedValue}`);
    });
    
    // Process all rules to show which conditions were met
    rules.forEach(rule => {
      if (rule.event === 'log') {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          const operator = condition.operator || '=';
          const threshold = condition.value !== undefined ? condition.value : 'N/A';
          const actualValue = event[field] !== undefined ? event[field] : 'N/A';
          
          // Format field name for display
          const fieldDisplay = field.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          // For log alerts, show the actual value that matched
          ruleSummaries.push(`${fieldDisplay} ${operator} ${threshold}${actualValue !== 'N/A' ? ` (Matched: ${actualValue})` : ''}`);
        });
      }
    });
    
    // Extract the most important part of the log message for the summary
    let logSummary = '';
    if (logMessage !== 'N/A') {
      // Truncate long log messages for the summary
      logSummary = logMessage.length > 40 ? logMessage.substring(0, 37) + '...' : logMessage;
    }
    
    message = `${logLevel}${logSummary ? `: ${logSummary}` : ''} | ${conciseTime}`;
    detailedInfo = logProperties.length > 0 ? '\n' + logProperties.join('\n') : '';
    
  } else if (sourceType === 'Global') {
    // For global/connection events
    const connectionType = event.type || event.connection_type || 'Unknown';
    const connectionStatus = event.status || event.connection_status || 'status change';
    
    // Extract all connection properties dynamically
    const connectionProperties = [];
    const displayedFields = new Set(['type', 'connection_type', 'status', 'connection_status']);
    
    // Add primary fields first
    connectionProperties.push(`Connection type: ${connectionType}`);
    connectionProperties.push(`Status: ${connectionStatus}`);
    
    // Process any additional fields
    Object.entries(event).forEach(([key, value]) => {
      // Skip fields we've already processed and common non-connection fields
      if (displayedFields.has(key) || 
          ['id', 'server_id', 'serverId', 'event', 'timestamp', 'event_time', 'hostname'].includes(key)) {
        return;
      }
      
      // Format the display name
      const displayName = key.split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Format the value based on type
      let formattedValue = value;
      if (typeof value === 'number') {
        formattedValue = value.toFixed(2);
      } else if (value === null || value === undefined) {
        formattedValue = 'N/A';
      } else if (typeof value === 'object') {
        try {
          formattedValue = JSON.stringify(value);
        } catch (e) {
          formattedValue = '[Complex Object]';
        }
      }
      
      connectionProperties.push(`${displayName}: ${formattedValue}`);
    });
    
    // Process any rules for connection events
    rules.forEach(rule => {
      if (rule.event === 'connection' || rule.event === 'global') {
        const conditions = rule.conditions || [];
        conditions.forEach(condition => {
          const field = condition.field || 'Unknown';
          const operator = condition.operator || '=';
          const threshold = condition.value !== undefined ? condition.value : 'N/A';
          const actualValue = event[field] !== undefined ? event[field] : 'N/A';
          
          // Format field name for display
          const fieldDisplay = field.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          ruleSummaries.push(`${fieldDisplay} ${operator} ${threshold}${actualValue !== 'N/A' ? ` (Current: ${actualValue})` : ''}`);
        });
      }
    });
    
    // Add minimal connection info
    let connectionCode = event.code !== undefined ? `/${event.code}` : '';
    
    message = `${connectionType}:${connectionStatus}${connectionCode} | ${conciseTime}`;
    detailedInfo = connectionProperties.length > 0 ? '\n' + connectionProperties.join('\n') : '';
  } else {
    // Fallback for other event types
    message = `${sourceType}:${alertName} | ${conciseTime}`;
  }
  
  // Add rule conditions summary if available
  if (ruleSummaries.length > 0) {
    detailedInfo += '\n\nTriggered by conditions:';
    ruleSummaries.forEach((summary, index) => {
      detailedInfo += `\n  ${index + 1}. ${summary}`;
    });
  }
  
  // Add custom description from the alert if available
  if (alert.description) {
    detailedInfo += `\n\nAlert description: ${alert.description}`;
  }
  
  // Combine message and detailed info
  const finalMessage = message + detailedInfo;
  
  const notification = createNotification({
    alertId: alert.id,
    title: enhancedTitle,
    message: finalMessage,
    severity: alert.severity,
    source: {
      type: sourceType,
      data: event
    },
    userId: alert.userId
  });
  
  console.log('Created notification:', notification);
  return notification;
}

/**
 * Gets the display text for an alert severity level.
 * 
 * @param {string} severity - The severity level
 * @returns {string} Human-readable text for the severity level
 */
function getSeverityDisplayText(severity) {
  switch (severity) {
    case AlertSeverity.INFO:
      return 'INFO';
    case AlertSeverity.WARNING:
      return 'WARN';
    case AlertSeverity.CRITICAL:
      return 'CRIT';
    default:
      return severity;
  }
}

/**
 * Gets the CSS class for an alert severity level.
 * 
 * @param {string} severity - The severity level
 * @returns {string} CSS class for the severity level
 */
function getSeverityCssClass(severity) {
  switch (severity) {
    case AlertSeverity.INFO:
      return 'alert-info';
    case AlertSeverity.WARNING:
      return 'alert-warning';
    case AlertSeverity.CRITICAL:
      return 'alert-critical';
    default:
      return 'alert-default';
  }
}

/**
 * Gets the icon component for an alert severity level.
 * 
 * @param {string} severity - The severity level
 * @returns {JSX.Element} Icon component for the severity level
 */
function getSeverityIcon(severity) {
  // Import icons directly in the function to avoid circular dependencies
  const { 
    InfoCircleIcon, 
    ExclamationTriangleIcon, 
    ExclamationCircleIcon, 
    BellIcon 
  } = require('../components/Icons');
  
  switch (severity) {
    case AlertSeverity.INFO:
      return <InfoCircleIcon />;
    case AlertSeverity.WARNING:
      return <ExclamationTriangleIcon />;
    case AlertSeverity.CRITICAL:
      return <ExclamationCircleIcon />;
    default:
      return <BellIcon />;
  }
}

/**
 * Gets the display text for an alert type.
 * 
 * @param {string} type - The alert type
 * @returns {string} Human-readable text for the alert type
 */
function getAlertTypeDisplayText(type) {
  switch (type) {
    case AlertType.GLOBAL:
      return 'Global Connection';
    case AlertType.SYSTEM_EVENT:
      return 'System Event';
    case AlertType.PROCESS:
      return 'Process';
    case AlertType.LOG:
      return 'Log';
    default:
      return type;
  }
}

/**
 * Gets the icon component for an alert type.
 * 
 * @param {string} type - The alert type
 * @returns {JSX.Element} Icon component for the alert type
 */
function getAlertTypeIcon(type) {
  // Import icons directly in the function to avoid circular dependencies
  const { 
    GlobeIcon, 
    ServerIcon, 
    MicrochipIcon, 
    FileAltIcon, 
    BellIcon 
  } = require('../components/Icons');
  
  switch (type) {
    case AlertType.GLOBAL:
      return <GlobeIcon />;
    case AlertType.SYSTEM_EVENT:
      return <ServerIcon />;
    case AlertType.PROCESS:
      return <MicrochipIcon />;
    case AlertType.LOG:
      return <FileAltIcon />;
    default:
      return <BellIcon />;
  }
}

/**
 * Checks if a process name matches a given pattern.
 * Supports exact match and wildcard patterns.
 * 
 * @param {string} processName - The process name to check
 * @param {string} pattern - The pattern to match against (supports * as wildcard)
 * @returns {boolean} True if the process name matches the pattern
 */
function matchProcessNamePattern(processName, pattern) {
  if (!processName || !pattern) return false;
  
  // Convert the glob-like pattern to a regular expression
  // Escape special regex characters in the pattern, except for *
  let regexPattern = pattern
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
    .replace(/\*/g, '.*'); // Convert * to .*
  
  // Determine if the pattern needs anchoring
  const startsWith = !pattern.startsWith('*');
  const endsWith = !pattern.endsWith('*');
  
  // Apply anchoring based on the pattern
  if (startsWith) regexPattern = '^' + regexPattern;
  if (endsWith) regexPattern = regexPattern + '$';
  
  const regex = new RegExp(regexPattern);
  return regex.test(processName);
}

/**
 * Gets the display text for a condition.
 * 
 * @param {string} condition - The condition
 * @returns {string} Human-readable text for the condition
 */
function getConditionDisplayText(condition) {
  switch (condition) {
    case AlertCondition.EQUALS:
      return 'equals';
    case AlertCondition.NOT_EQUALS:
      return 'does not equal';
    case AlertCondition.GREATER_THAN:
      return 'is greater than';
    case AlertCondition.LESS_THAN:
      return 'is less than';
    case AlertCondition.GREATER_THAN_OR_EQUALS:
      return 'is greater than or equal to';
    case AlertCondition.LESS_THAN_OR_EQUALS:
      return 'is less than or equal to';
    case AlertCondition.CONTAINS:
      return 'contains';
    default:
      return condition;
  }
}

// Export all models and functions
export {
  AlertType,
  AlertSeverity,
  AlertScopeType,
  AlertCondition,
  AlertAction,
  GlobalEvents,
  SystemEventEvents,
  ProcessEvents,
  LogEvents,
  createAlert,
  createNotification,
  createAlertRule,
  validateAlert,
  validateAlertRule,
  validateNotification,
  getEventsForAlertType,
  evaluateRule,
  evaluateAlert,
  createNotificationFromAlert,
  getSeverityDisplayText,
  getSeverityCssClass,
  getSeverityIcon,
  getAlertTypeDisplayText,
  getAlertTypeIcon,
  getConditionDisplayText,
  matchProcessNamePattern,
  generateUUID
};
